import { APP_TYPE } from '@intigral/constant/common';
import { AxiosCacheGlobalConfigs } from '@intigral/constant/config/AxiosCacheConfig';
import { DI } from '@intigral/constant/config/DIContainer';
import { setupCache } from 'axios-cache-interceptor';
import { container } from './DIContainer';
import { initializeServices } from './application/services/services';
import { IHttpAdapter } from './core/entities/iHttpAdapter';
import { initializeUseCases } from './core/usecases/usecases';
import AxiosHttp, {
    addLogInterceptorToHttpCalls,
    createAxiosInstance,
    addResponseInterceptorToHttpCalls,
} from './infrastructures/api/AxiosHttp';
import ApiPathManager, { IApiPathManager } from './infrastructures/config/ApiPathManager';
import { localConfig } from './infrastructures/config/localeConfigMapper';
import { intializeRepositories } from './infrastructures/repositories/repositories';
import { resolveWcpAppKey } from '../utils/resolveWcpAppKey';
// import user from '../../../../apps/wcp/src/helpers/user.js';

// Add the log interceptor to the Axios instance
const axiosInstance = addLogInterceptorToHttpCalls(setupCache(createAxiosInstance(''), AxiosCacheGlobalConfigs));

// addResponseInterceptorToHttpCalls(axiosInstance);

// Register the AxiosHttp adapter
container.register(DI.UTILS.IHttpAdapter, new AxiosHttp(axiosInstance));

const initializeDataDomain = async (appType: string) => {
    try {
        const httpAdapter = container.resolve<IHttpAdapter>(DI.UTILS.IHttpAdapter);

        if (appType === APP_TYPE.WCP) {
            const apiKey = resolveWcpAppKey();

            //  FIXED: Handle missing API key gracefully
            if (!apiKey) {
                // Check if we're already on login page to prevent infinite redirect loop
                const currentPath = window.location.pathname;
                const isOnLoginPage = currentPath.includes('/login') || currentPath.includes('/account');

                if (!isOnLoginPage) {
                    console.warn('⚠️ Redirecting to login page...');

                    // Construct login URL using the same pattern as appConfig
                    const currentLang = currentPath.includes('/en/') ? 'en' : '';
                    const baseUrl = window.location.origin;
                    const loginUrl = currentLang ? `${baseUrl}/en/login` : `${baseUrl}/login`;

                    // Use setTimeout to prevent interrupting the current execution flow
                    setTimeout(() => {
                        window.location.href = loginUrl;
                    }, 100);
                }

                // Still initialize with local config to prevent errors during redirect or for login page
                const apiPathManager = await ApiPathManager.createFromLocal(APP_TYPE.WCP, localConfig);
                container.register<IApiPathManager>(DI.UTILS.IApiPathManager, apiPathManager);

                intializeRepositories(container);
                initializeUseCases(container);
                initializeServices(container);

                return { success: true, configuration: null };
            }

            addResponseInterceptorToHttpCalls(axiosInstance);
            const baseUrl = process.env['NX_PUBLIC_GATEWAY_API_BASE_URL'];
            const cmsConfigUrl = `${baseUrl}/v2/${apiKey}/env?deviceType=pc&platform=browser`;

            try {
                const { instance: apiPathManager, configuration } = await ApiPathManager.createFromCMS(
                    APP_TYPE.WCP,
                    cmsConfigUrl,
                    httpAdapter
                );
                container.register<IApiPathManager>(DI.UTILS.IApiPathManager, apiPathManager);

                // Continue with the rest of initialization
                intializeRepositories(container);
                initializeUseCases(container);
                initializeServices(container);

                // Return configuration data so it can be saved to React Query
                return { success: true, configuration };
            } catch (error) {
                console.warn('Failed to initialize API paths from CMS:', error);
                console.warn('⚠️ Falling back to local configuration...');

                // Fallback to local configuration to ensure APIs still work
                try {
                    const apiPathManager = await ApiPathManager.createFromLocal(APP_TYPE.WCP, localConfig);
                    container.register<IApiPathManager>(DI.UTILS.IApiPathManager, apiPathManager);

                    // Continue with the rest of initialization
                    intializeRepositories(container);
                    initializeUseCases(container);
                    initializeServices(container);

                    return { success: true, configuration: null };
                } catch (fallbackError) {
                    console.error('Failed to initialize with local configuration:', fallbackError);
                    throw fallbackError;
                }
            }
        } else if (appType === APP_TYPE.SLP_MLP) {
            addResponseInterceptorToHttpCalls(axiosInstance);
            const apiPathManager = await ApiPathManager.createFromLocal(APP_TYPE.SLP_MLP, localConfig);
            container.register<IApiPathManager>(DI.UTILS.IApiPathManager, apiPathManager);

            // Continue with the rest of initialization
            intializeRepositories(container);
            initializeUseCases(container);
            initializeServices(container);

            // For SLP_MLP, no configuration is returned
            return { success: true, configuration: null };
        } else if (appType === APP_TYPE.TV) {
            // TV app initialization: no cookie/session auth, no login redirect
            // TV apps authenticate via QR code or trusted login after domain init
            const baseUrl = process.env['NX_PUBLIC_GATEWAY_API_BASE_URL'];
            const tvStaticApiKey = process.env['NX_PUBLIC_TV_STATIC_API_KEY'] || 'skyWebOSB2CGDMSTGExy0sVDlZMzNDd';
            const appKey = process.env['NX_PUBLIC_APP_KEY'] || 'lgWebOSStgJ21';

            if (baseUrl) {
                addResponseInterceptorToHttpCalls(axiosInstance);
                const cmsConfigUrl = `${baseUrl}/v2/${tvStaticApiKey}/env?appKey=${appKey}&platform=webOs&deviceType=tv`;

                try {
                    const { instance: apiPathManager, configuration } = await ApiPathManager.createFromCMS(
                        APP_TYPE.TV,
                        cmsConfigUrl,
                        httpAdapter
                    );
                    container.register<IApiPathManager>(DI.UTILS.IApiPathManager, apiPathManager);

                    intializeRepositories(container);
                    initializeUseCases(container);
                    initializeServices(container);

                    return { success: true, configuration };
                } catch (error) {
                    console.error('TV: Failed to initialize API paths from CMS:', error);
                    console.warn('⚠️ TV: Falling back to local configuration...');

                    try {
                        const apiPathManager = await ApiPathManager.createFromLocal(APP_TYPE.TV, localConfig);
                        container.register<IApiPathManager>(DI.UTILS.IApiPathManager, apiPathManager);

                        intializeRepositories(container);
                        initializeUseCases(container);
                        initializeServices(container);

                        return { success: true, configuration: null };
                    } catch (fallbackError) {
                        console.error('TV: Failed to initialize with local configuration:', fallbackError);
                        throw fallbackError;
                    }
                }
            } else {
                // No base URL or app key — initialize with local config
                console.warn('⚠️ TV: Missing NX_PUBLIC_GATEWAY_API_BASE_URL or NX_PUBLIC_APP_KEY, using local config');
                const apiPathManager = await ApiPathManager.createFromLocal(APP_TYPE.TV, localConfig);
                container.register<IApiPathManager>(DI.UTILS.IApiPathManager, apiPathManager);

                intializeRepositories(container);
                initializeUseCases(container);
                initializeServices(container);

                return { success: true, configuration: null };
            }
        }
        return { success: false, configuration: null };
    } catch (error) {
        console.error('Failed to initialize services:', error);
        return { success: false, configuration: null };
    }
};

export { container, initializeDataDomain };
